import React, { useRef, useEffect, useState } from "react";
import { useOnboardingStore } from "../../../store/onboardingStore";
import styles from "./Step5ReviewSignature.module.css";

export const Step5ReviewSignature: React.FC = () => {
    const { formData, errors, updateFormField, clearError } = useOnboardingStore();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(!!formData.signatureData);

    // Initialize canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Set canvas size
        canvas.width = canvas.offsetWidth;
        canvas.height = 250;

        // Set white background
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Set border
        ctx.strokeStyle = "#E5E7EB";
        ctx.lineWidth = 1;
        ctx.strokeRect(0, 0, canvas.width, canvas.height);

        // Restore existing signature if available
        if (formData.signatureData) {
            const img = new Image();
            img.src = formData.signatureData;
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
            };
        }
    }, [formData.signatureData]);

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        setIsDrawing(true);
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        ctx.lineTo(x, y);
        ctx.strokeStyle = "#2563EB";
        ctx.lineWidth = 3;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();
    };

    const stopDrawing = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.closePath();
        setIsDrawing(false);

        // Save signature
        const signatureData = canvas.toDataURL("image/png");
        updateFormField("signatureData", signatureData);
        setHasSignature(true);

        // Clear error when signature is added
        if (errors.has("signatureData")) {
            clearError("signatureData");
        }
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.strokeStyle = "#E5E7EB";
        ctx.lineWidth = 1;
        ctx.strokeRect(0, 0, canvas.width, canvas.height);

        updateFormField("signatureData", "");
        setHasSignature(false);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.iconBox}>
                    <svg className={styles.icon} fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
                <div>
                    <h2 className={styles.title}>Electronic Signature</h2>
                    <p className={styles.subtitle}>Sign to complete your registration</p>
                </div>
            </div>

            <div className={styles.content}>
                {/* Signature Canvas */}
                <div className={styles.signatureSection}>
                    <label className={styles.label}>
                        Sign Below
                        <span className={styles.required}>*</span>
                    </label>

                    <canvas
                        ref={canvasRef}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        className={`${styles.canvas} ${errors.get("signatureData") ? styles.error : ""}`}
                    />

                    {errors.get("signatureData") && <p className={styles.errorText}>{errors.get("signatureData")}</p>}

                    <button type="button" onClick={clearSignature} className={styles.clearButton}>
                        Clear Signature
                    </button>
                </div>

                {/* Terms and Agreements */}
                <div className={styles.termsSection}>
                    <h3 className={styles.termsTitle}>Terms and Agreements</h3>
                    <p className={styles.termsSubtitle}>By signing above, you agree to the following:</p>

                    <ul className={styles.termsList}>
                        <li>All information provided is accurate and truthful</li>
                        <li>You have read and agree to our Terms of Service</li>
                        <li>You consent to electronic communications</li>
                        <li>You authorize ACH transactions for payments</li>
                        <li>You understand the account is subject to approval</li>
                    </ul>
                </div>

                {/* Agree to Terms Checkbox */}
                <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={(e) => {
                            updateFormField('agreeToTerms', e.target.checked);
                            if (errors.has('agreeToTerms')) {
                                clearError('agreeToTerms');
                            }
                        }}
                        className={styles.checkbox}
                    />
                    <label htmlFor="agreeToTerms" className={styles.checkboxLabel}>
                        I agree to all terms and conditions
                        <span className={styles.required}>*</span>
                    </label>
                </div>
                {errors.get("agreeToTerms") && <p className={styles.errorText}>{errors.get("agreeToTerms")}</p>}

                {/* Info Box */}
                <div className={styles.infoBox}>
                    <div className={styles.infoIcon}>
                        <svg fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                    <div>
                        <p className={styles.infoTitle}>What happens after submission?</p>
                        <p className={styles.infoText}>
                            Your application will be reviewed within 1-2 business days. You'll receive an email
                            notification once your account has been approved and is ready to use.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
